/**
 * CSV export of the CI daily report.
 *
 * Reads the finished `PacDailyReport` and nothing else. Every figure here is the
 * one already on screen, so an export cannot disagree with the dashboard — the
 * moment this file re-derives anything, the two start drifting and neither can
 * be trusted.
 *
 * The file contains patient names and phone numbers. It is PHI leaving the app:
 * no analytics event may carry its contents, and the filename deliberately names
 * only the date.
 */

import { PacCallDisposition, PacDailyReport, Ratio, ratioPercent } from './pac-daily-report';

/**
 * Escapes one field: quoted when it holds a comma, quote or newline, and
 * defused when a spreadsheet would read it as a formula.
 *
 * Excel and Sheets evaluate any cell whose value starts with `=`, `+`, `-` or
 * `@`, quoted or not. Every caller number here is E.164, so without this every
 * `+13135551234` in the file renders as `13135551234` — the `+1` silently gone —
 * and a patient whose name happens to start with `=` becomes a live formula in a
 * file someone opens on a work laptop. A leading apostrophe is the standard
 * defusal; the spreadsheet strips it on display.
 */
function csvEscape(value: string): string {
  const defused = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
  return /[",\r\n]/.test(defused) ? `"${defused.replace(/"/g, '""')}"` : defused;
}

function csvRow(cells: string[]): string {
  return cells.map(csvEscape).join(',');
}

/** `4:12`, matching the on-screen call length. */
function formatDurationCell(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
}

/**
 * A ratio becomes three columns: numerator, denominator, percent.
 *
 * A spreadsheet cannot recompute anything from `"22/35"`, and a lone percentage
 * hides how many calls it was measured over. An empty percent cell is the
 * zero-denominator case — deliberately blank rather than `0%`, which would read
 * as a real and terrible result.
 */
function ratioCells(ratio: Ratio | undefined): string[] {
  if (!ratio) {
    return ['', '', ''];
  }
  const percent = ratioPercent(ratio);
  return [String(ratio.numerator), String(ratio.denominator), percent === undefined ? '' : String(percent)];
}

const DISPOSITION_LABEL: Record<PacCallDisposition, string> = {
  'picked-up': 'Picked up',
  missed: 'Missed',
  forwarded: 'Forwarded',
  'in-progress': 'In progress',
};

const COORDINATOR_HEADERS = [
  'Coordinator',
  'Calls picked up',
  'Calls routed',
  'Pickup %',
  'Intro calls shown',
  'Intro calls booked',
  'Intro call show %',
  'Consults booked',
  'Conversations',
  'Consult conversion %',
  'Talk time (seconds)',
  'Talk time',
  'Rostered (seconds)',
  'Occupancy %',
];

const CALL_HEADERS = [
  'Date',
  'Time',
  'Timezone',
  'Hour bucket',
  'Caller',
  'Caller number',
  'Patient',
  'Disposition',
  'Message taken',
  'Answered by',
  'Duration (seconds)',
  'Duration',
  'Recording segments',
];

const INTRO_CALL_HEADERS = [
  'Date',
  'Time',
  'Timezone',
  'Patient',
  'Patient reference',
  'Coordinator',
  'Status',
  'Duration (minutes)',
  'Zoom meeting ID',
];

export interface PacDailyReportCsvOptions {
  /** The report's day, `YYYY-MM-DD` in `timezone`. */
  date: string;
  /** IANA zone every time in the file is expressed in, and named in its own column. */
  timezone: string;
  /** `Patient/<id>` → display name. */
  patientNames?: Record<string, string>;
  /** `Practitioner/<id>` → display name. */
  practitionerNames?: Record<string, string>;
  /**
   * Formats an ISO instant as a local time in `timezone`. Injected because this
   * module has no timezone library of its own and the caller already has one
   * configured for the dashboard.
   */
  formatTime: (isoInstant: string) => string;
}

/**
 * The whole day as one CSV, in three labelled blocks.
 *
 * Blocks rather than one wide table because a coordinator row and a call row
 * share almost no columns; padding them into a single shape would produce a file
 * that is mostly empty cells. Hero tiles are absent on purpose — every one of
 * them is an aggregate of the blocks below, so exporting them would create a
 * second place for the same number to be wrong.
 */
export function buildCiDailyReportCsv(report: PacDailyReport, options: PacDailyReportCsvOptions): string {
  const { date, timezone, patientNames = {}, practitionerNames = {}, formatTime } = options;

  const nameOf = (reference: string | undefined, names: Record<string, string>): string =>
    reference ? (names[reference] ?? reference) : '';

  const lines: string[] = [];

  lines.push(csvRow(['Coordinators']));
  lines.push(csvRow(COORDINATOR_HEADERS));
  for (const row of report.coordinators) {
    lines.push(
      csvRow([
        row.name,
        ...ratioCells(row.pickedUp),
        ...ratioCells(row.introCalls),
        ...ratioCells(row.consults),
        String(row.talkTimeSeconds),
        formatDurationCell(row.talkTimeSeconds),
        row.occupancy ? String(row.occupancy.denominator) : '',
        row.occupancy ? (ratioPercent(row.occupancy)?.toString() ?? '') : '',
      ])
    );
  }
  // Not a column sum, and labelled so nobody adds the rows and finds a different
  // number: a call offered to three coordinators is 3 routed but 1 inbound call.
  lines.push(
    csvRow([
      'Team total (not a column sum)',
      ...ratioCells(report.teamTotal.pickedUp),
      ...ratioCells(report.teamTotal.introCalls),
      ...ratioCells(report.teamTotal.consults),
      String(report.teamTotal.talkTimeSeconds),
      formatDurationCell(report.teamTotal.talkTimeSeconds),
      report.teamTotal.occupancy ? String(report.teamTotal.occupancy.denominator) : '',
      report.teamTotal.occupancy ? (ratioPercent(report.teamTotal.occupancy)?.toString() ?? '') : '',
    ])
  );

  lines.push('');
  lines.push(csvRow(['Call log']));
  lines.push(csvRow(CALL_HEADERS));
  for (const bucket of report.hours) {
    for (const call of bucket.calls) {
      lines.push(
        csvRow([
          date,
          formatTime(call.sent),
          timezone,
          String(bucket.hour),
          nameOf(call.patientReference, patientNames),
          call.callerNumber ?? '',
          call.patientReference ?? '',
          DISPOSITION_LABEL[call.disposition],
          call.messageTaken ? 'Yes' : '',
          nameOf(call.answeredBy, practitionerNames),
          String(call.durationSeconds),
          // A call still in progress has no length yet, and a zero would read as
          // one that lasted no time at all.
          call.disposition === 'in-progress' ? '' : formatDurationCell(call.durationSeconds),
          String(call.recordings.length),
        ])
      );
    }
  }

  lines.push('');
  lines.push(csvRow(['Scheduled intro calls']));
  lines.push(csvRow(INTRO_CALL_HEADERS));
  for (const bucket of report.hours) {
    for (const introCall of bucket.introCalls) {
      lines.push(
        csvRow([
          date,
          formatTime(introCall.start),
          timezone,
          nameOf(introCall.patientReference, patientNames),
          introCall.patientReference ?? '',
          nameOf(introCall.practitionerReference, practitionerNames),
          introCall.status,
          introCall.durationMinutes ? String(introCall.durationMinutes) : '',
          introCall.meetingId ?? '',
        ])
      );
    }
  }

  return lines.join('\r\n');
}

/**
 * `ci-daily-report-2026-07-09.csv`.
 *
 * Names the day and nothing else — a patient identifier in a filename survives
 * in download histories, Slack previews and email subject lines long after the
 * file itself is gone.
 */
export function pacDailyReportCsvFilename(date: string): string {
  return `ci-daily-report-${date}.csv`;
}
